<template>
	<article class="forum-thread-card" :class="{ 'forum-pinned': thread.isPinned }" @click="onCardClick">
		<header class="forum-thread-header">
			<div class="forum-avatar" :style="avatarStyle" :title="authorEmail">{{ avatarLetter }}</div>
			<div class="forum-thread-headmeta">
				<div class="forum-thread-author-row">
					<span class="forum-author" :title="authorEmail">{{ authorName }}</span>
					<span v-if="authorHandle" class="forum-author-handle">@{{ authorHandle }}</span>
					<span v-if="thread.isPinned" class="forum-pinned-badge">
						<i class="fas fa-thumbtack"></i> Pinned
					</span>
				</div>
				<div class="forum-thread-sub">
					<span class="forum-time" :title="thread.createdAt">{{ formatTime(thread.createdAt) }}</span>
					<span v-if="isEdited" class="forum-sep">·</span>
					<span v-if="isEdited" class="forum-edited-badge" :title="`Edited ${thread.updatedAt}`">edited</span>
					<span v-if="thread.viewCount != null" class="forum-sep">·</span>
					<span v-if="thread.viewCount != null" class="forum-viewcount">
						<i class="far fa-eye"></i> {{ thread.viewCount }}
					</span>
				</div>
			</div>
		</header>

		<h3 class="forum-thread-title">{{ thread.title }}</h3>

		<!-- Markdown body, rendered to safe HTML once at compute time. -->
		<div class="forum-thread-body rich-content" v-html="renderedBody"></div>

		<div class="forum-thread-actions">
			<button
				type="button"
				class="forum-action"
				:class="{
					'forum-action-reacted': thread.hasReacted,
					'reaction-bumping': isReactionBumping,
					'forum-action-locked': isLikeLocked,
				}"
				:disabled="isLikeLocked"
				:title="reactButtonTitle"
				@click="onReact"
			>
				<i :class="thread.hasReacted ? 'fas fa-thumbs-up' : 'far fa-thumbs-up'"></i>
				<span class="forum-action-label">{{ thread.hasReacted ? "Liked" : "Like" }}</span>
				<span v-if="(thread.reactionCount || 0) > 0" class="forum-action-count">{{ thread.reactionCount }}</span>
			</button>

			<button
				type="button"
				class="forum-action"
				:class="{ 'forum-action-locked': isCommentLocked }"
				:disabled="isCommentLocked"
				:title="commentButtonTitle"
				@click="openDetail"
			>
				<i class="far fa-comment"></i>
				<span class="forum-action-label">Comment</span>
				<span v-if="(thread.commentCount || 0) > 0" class="forum-action-count">{{ thread.commentCount }}</span>
			</button>

			<button
				v-if="isModerator && boardActive"
				type="button"
				class="forum-action"
				:class="{ 'forum-action-pinned': thread.isPinned }"
				:title="thread.isPinned ? 'Unpin thread' : 'Pin thread'"
				@click="$emit('pin', thread)"
			>
				<i class="fas fa-thumbtack"></i>
				<span class="forum-action-label">{{ thread.isPinned ? "Unpin" : "Pin" }}</span>
			</button>
		</div>
	</article>
</template>

<script>
import { mapState } from "vuex";
import { UserType } from "../../constants/user";
import { AVATAR_COLORS } from "../../constants/forum";
import { markdownToHTML } from "../../utils/markdown";
import { registerAttachments } from "../../utils/attachment-registry";

export default {
	name: "ThreadListItem",
	props: {
		thread: { type: Object, required: true },
		productVariantId: { type: [Number, String], default: null },
		boardActive: { type: Boolean, default: true },
	},
	data() {
		return {
			isReactionBumping: false,
		};
	},
	computed: {
		...mapState("user", ["user"]),
		isModerator() {
			const userType = this.user && this.user.type;
			if (this.equalsIgnoreCase(userType, UserType.MANAGEMENT)) return true;
			if (this.equalsIgnoreCase(userType, UserType.TRAINER)) return this.productVariantId != null;
			return false;
		},
		isLikeLocked() {
			return !this.boardActive || !this.thread.isReactionAllowed;
		},
		isCommentLocked() {
			return !this.boardActive || !this.thread.isCommentAllowed;
		},
		reactButtonTitle() {
			if (!this.boardActive) return "Forum is disabled — no new likes can be added.";
			if (!this.thread.isReactionAllowed) return "Reactions are locked on this thread.";
			return this.thread.hasReacted ? "Unlike" : "Like";
		},
		commentButtonTitle() {
			if (!this.boardActive) return "Forum is disabled — no new comments can be added.";
			if (!this.thread.isCommentAllowed) return "Comments are locked on this thread.";
			return "Comment";
		},
		author() {
			return this.thread.author || null;
		},
		// Prefer "First Last" -> "First" -> "@username" -> fallback.
		authorName() {
			const author = this.author;
			if (!author) return "Anonymous";
			const firstName = (author.firstName || "").trim();
			const lastName  = (author.lastName  || "").trim();
			if (firstName || lastName) return `${firstName} ${lastName}`.trim();
			if (author.username) return author.username;
			return "Anonymous";
		},
		authorHandle() {
			// Only render the @handle line if there's a real full name above
			// it; otherwise the username already IS the name.
			if (!this.author) return "";
			const hasFull = (this.author.firstName || "").trim()
				|| (this.author.lastName || "").trim();
			return hasFull && this.author.username ? this.author.username : "";
		},
		authorEmail() {
			return this.author?.email || "";
		},
		avatarLetter() {
			return this.authorName.trim().charAt(0).toUpperCase() || "A";
		},
		avatarStyle() {
			const colorIndex = ((this.thread.createdBy || 0) + (this.avatarLetter.charCodeAt(0) || 0)) % AVATAR_COLORS.length;
			return { backgroundColor: AVATAR_COLORS[colorIndex] };
		},
		isEdited() {
			if (!this.thread.updatedAt) return false;
			if (!this.thread.createdAt) return false;
			return new Date(this.thread.updatedAt).getTime() > new Date(this.thread.createdAt).getTime() + 1000;
		},
		// Bodies are stored as markdown going forward. Old HTML rows still
		// render correctly — markdown-it has html: true so existing tags
		// pass through unchanged. Attachments are registered first so any
		// inline [[file:id]] / [[image:id]] tokens find their file URLs.
		renderedBody() {
			registerAttachments(this.thread.attachments || []);
			return markdownToHTML(this.thread.body || "");
		},
	},
	methods: {
		equalsIgnoreCase(a, b) {
			if (a == null || b == null) return a === b;
			return String(a).toLowerCase() === String(b).toLowerCase();
		},
		// Card click navigates to detail. Buttons/links and their inner
		// <i>/<span> are excluded via closest(); :disabled buttons still
		// bubble (we set pointer-events: auto so the not-allowed cursor
		// renders), so closest() catches them too.
		onCardClick(event) {
			if (event.target.closest("button, a")) return;
			this.openDetail();
		},
		openDetail() {
			if (!this.thread || !this.thread.id) return;
			// When the thread list is rendered inside the admin shell
			// (/management/forum/...), push to the admin-flagged twin so
			// the global beforeEach guard doesn't kick management/trainer
			// users out to /management/course.
			const targetName = this.$route.meta && this.$route.meta.isAdmin
				? "ThreadDetailAdmin"
				: "ThreadDetail";
			this.$router.push({ name: targetName, params: { id: this.thread.id } });
		},
		onReact() {
			if (!this.thread.hasReacted) {
				this.isReactionBumping = true;
				setTimeout(() => { this.isReactionBumping = false; }, 600);
			}
			this.$emit("react", this.thread);
		},
		formatTime(iso) {
			if (!iso) return "";
			const timestamp = new Date(iso);
			const diffMs = Date.now() - timestamp.getTime();
			const diffMinutes = Math.floor(diffMs / 60000);
			if (diffMinutes < 1) return "just now";
			if (diffMinutes < 60) return `${diffMinutes}m`;
			const diffHours = Math.floor(diffMinutes / 60);
			if (diffHours < 24) return `${diffHours}h`;
			const diffDays = Math.floor(diffHours / 24);
			if (diffDays < 7) return `${diffDays}d`;
			return timestamp.toLocaleDateString();
		},
	},
};
</script>

<style scoped>
.forum-thread-card {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 12px 16px 4px;
	border: 1px solid #ececec;
	transition: border-color 0.25s cubic-bezier(0.4, 0, 0.2, 1),
	            box-shadow 0.25s cubic-bezier(0.4, 0, 0.2, 1);
	font-family: "Roboto", -apple-system, BlinkMacSystemFont, "Segoe UI", Arial, sans-serif;
	color: #0f0f0f;
	cursor: pointer;
}

.forum-thread-card:hover {
	border-color: #e0e0e0;
	box-shadow: 0 1px 10px rgba(0, 0, 0, 0.04);
}

.forum-thread-card.forum-pinned {
	box-shadow: none;
}

.forum-thread-header {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 8px;
}

.forum-avatar {
	width: 34px;
	height: 34px;
	border-radius: 50%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #ffffff;
	font-weight: 600;
	font-size: 14px;
	flex-shrink: 0;
	letter-spacing: -0.02em;
}

.forum-thread-headmeta {
	display: flex;
	flex-direction: column;
	min-width: 0;
	flex: 1;
	line-height: 1.2;
}

.forum-thread-author-row {
	display: flex;
	align-items: center;
	gap: 8px;
	flex-wrap: wrap;
}

.forum-author {
	font-size: 13px;
	font-weight: 500;
	color: #0f0f0f;
}

.forum-author-handle {
	font-size: 12px;
	color: #606060;
	font-weight: 400;
}

.forum-pinned-badge {
	display: inline-flex;
	align-items: center;
	gap: 4px;
	background-color: rgba(0, 0, 0, 0.05);
	color: #606060;
	padding: 1px 7px;
	border-radius: 4px;
	font-size: 10px;
	font-weight: 500;
	letter-spacing: 0.03em;
	text-transform: uppercase;
}

.forum-edited-badge {
	font-style: italic;
	color: #909090;
}

.forum-thread-sub {
	display: flex;
	align-items: center;
	gap: 6px;
	font-size: 11.5px;
	color: #606060;
	margin-top: 2px;
}

.forum-sep {
	color: #909090;
}

.forum-viewcount {
	display: inline-flex;
	align-items: center;
	gap: 4px;
}

.forum-thread-title {
	font-size: 15px;
	font-weight: 600;
	color: #0f0f0f;
	margin: 0 0 6px;
	line-height: 1.35;
	cursor: pointer;
	letter-spacing: -0.005em;
	transition: color 0.2s ease;
}

.forum-thread-title:hover {
	color: var(--primary-color, #0f0f0f);
}

.forum-thread-body {
	color: #0f0f0f;
	font-size: 13.5px;
	line-height: 1.5;
	margin-bottom: 8px;
}

.forum-thread-actions {
	display: flex;
	gap: 4px;
	padding: 4px 0 6px;
	margin-top: 2px;
	border-top: 1px solid #f2f2f2;
	padding-top: 6px;
}

.forum-action {
	display: inline-flex;
	align-items: center;
	gap: 7px;
	padding: 6px 12px;
	background-color: transparent;
	border: none;
	border-radius: 999px;
	font-size: 12.5px;
	font-weight: 500;
	color: #606060;
	cursor: pointer;
	transition: background-color 0.2s ease, color 0.2s ease;
	font-family: inherit;
}

.forum-action:hover {
	background-color: rgba(0, 0, 0, 0.05);
	color: #0f0f0f;
}

.forum-action.forum-action-reacted {
	background-color: rgba(0, 0, 0, 0.05);
	color: var(--secondary-color, #0f0f0f);
}

.forum-action.forum-action-reacted i {
	color: var(--primary-color, #065fd4);
}

/* Locked: thread has reactions or comments disabled by a moderator. We
   keep an already-liked thread at full opacity so the "you liked this"
   highlight survives — only the un-liked locked state desaturates.
   `pointer-events: auto` overrides Chrome's UA default of
   `pointer-events: none` on :disabled form controls — without it the
   `cursor: not-allowed` rule never reaches the user. The `disabled`
   attribute still blocks click events independently of pointer-events. */
.forum-action.forum-action-locked,
.forum-action[disabled] {
	cursor: not-allowed;
	opacity: 0.7;
	pointer-events: auto;
}
.forum-action.forum-action-locked:hover,
.forum-action[disabled]:hover {
	background-color: transparent;
	color: #606060;
}
.forum-action.forum-action-reacted.forum-action-locked,
.forum-action.forum-action-reacted[disabled] { opacity: 1; }

/* Like pop — JS-controlled .reaction-bumping class fires only on the
   click that turns liked-on, not on initial render of already-liked
   threads. The bezier overshoots slightly for a snappy bounce. */
.forum-action i {
	display: inline-block;
	transition: color 0.18s ease, transform 0.18s ease;
}
.forum-action.reaction-bumping i {
	animation: forum-thread-reaction-pop 0.55s cubic-bezier(0.34, 1.56, 0.64, 1);
	color: var(--primary-color, #ffd83f);
}
@keyframes forum-thread-reaction-pop {
	0%   { transform: scale(1); }
	18%  { transform: scale(0.82); }
	55%  { transform: scale(1.42); }
	100% { transform: scale(1); }
}
@media (prefers-reduced-motion: reduce) {
	.forum-action.reaction-bumping i { animation: none; }
}

.forum-action i {
	font-size: 14px;
}

.forum-action-label {
	line-height: 1;
}

.forum-action-count {
	font-variant-numeric: tabular-nums;
	color: inherit;
	font-weight: 600;
	padding-left: 2px;
}

/* Rich-HTML scoping (markdown-rendered content). */
.forum-thread-card .rich-content p { margin: 0 0 8px 0; }
.forum-thread-card .rich-content p:last-child { margin-bottom: 0; }
.forum-thread-card .rich-content pre {
	background-color: #f8f8f8;
	padding: 12px 14px;
	border-radius: 8px;
	font-size: 13px;
	overflow-x: auto;
	margin: 8px 0;
	border: 1px solid #ececec;
}
.forum-thread-card .rich-content code {
	background-color: rgba(0, 0, 0, 0.05);
	padding: 2px 6px;
	border-radius: 4px;
	font-size: 13px;
	font-family: "Roboto Mono", "SFMono-Regular", Consolas, monospace;
}
.forum-thread-card .rich-content a {
	color: var(--primary-color, #065fd4);
	text-decoration: none;
}
.forum-thread-card .rich-content a:hover { text-decoration: underline; }
.forum-thread-card .rich-content ul,
.forum-thread-card .rich-content ol { padding-left: 22px; margin: 8px 0; }
.forum-thread-card .rich-content li { margin-bottom: 4px; }
.forum-thread-card .rich-content blockquote {
	border-left: 3px solid #e0e0e0;
	padding-left: 14px;
	margin: 10px 0;
	color: #606060;
}
.forum-thread-card .rich-content strong { font-weight: 600; }
.forum-thread-card .rich-content img { max-width: 100%; border-radius: 6px; }
.forum-thread-card .rich-content .mention {
	background-color: rgba(3, 50, 77, 0.1);
	color: var(--secondary-color, #03324d);
	padding: 1px 6px;
	border-radius: 4px;
	font-weight: 500;
	white-space: nowrap;
}

/* Mobile: tighter card padding so cards span the full width. */
@media (max-width: 600px) {
	.forum-thread-card { padding: 10px 12px 4px; border-radius: 10px; }
	.forum-thread-title { font-size: 14.5px; }
	.forum-thread-body { font-size: 13px; }
	.forum-action { padding: 6px 10px; font-size: 12px; }
}
</style>
